/**
 * bushfire.io's fire aircraft and fire incidents (docs/specs/map.md §31; MB's notes `reference/python/BUSHFIRE_IO.md`).
 *
 * Terms: bushfire.io's Terms of Service allow personal, non-commercial use only without a written agreement, and its
 * aircraft positions come from Flightradar24 (§19.6). MB decided (decision 476) to use it for Thomson Airborne only
 * while he asks Disaster Science (`docs/reference/letters/bushfire-io.md`), and to stop if permission is refused. So it
 * is behind two switches: `BUSHFIRE_IO_ON` here (false removes every use at the next release) and the organisation's
 * switch in Teslyn Cloud (`tenant_service_switch` service `bushfireio`, on for Thomson only). Removing it for good is
 * deleting this file, `src-tauri/src/bushfire.rs`, `teslyn_core::map::mvt` and the places that name `bushfireio`.
 *
 * Teslyn Field reads bushfire.io's public vector tiles (`tiley.bushfire.io/v1/tiles/standard-v1/{z}/{x}/{y}`, no key,
 * `cache-control: public`) for the map view: the finest zoom at which the view fits in at most four tiles. Its Rust
 * core decodes the `adsb` and `incidents` layers (`teslyn_core::map::mvt`) and keeps each tile 30 seconds, so the
 * aircraft and the fire layer share one download. Here the features are read:
 *
 * - `adsb`: one point per aircraft bushfire.io follows (nearly all on NAFC's contract list), relayed from Flightradar24
 *   and about two minutes behind live. Its `body` is HTML lines "<strong>Key:</strong> value<br>": Callsign,
 *   Registration, Squawk, Nafc Callsign, Nafc Category, Nafc Role, Manufacturer, Model, Type Code, Operator.
 *   `active` is true for every aircraft, so `minsSinceActive` is the recency: over 10 minutes is dropped.
 * - `incidents`: a point per event and, where the agency maps one, the polygons of the fire ground under the same
 *   `eKey` (copies in neighbouring tiles joined). Fire and smoke events are kept; planned burns and burnt areas as
 *   planned burns. Where a state feed Teslyn reads already lists the incident, the state feed's is shown
 *   (`dedupeBushfireIncidents`).
 */
import type { GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';
import type { Parsed } from './tracker-adapters.js';
import { normaliseHex, normaliseRegistration, validFix, type TrackedPosition } from './tracker.js';
import { fireLevelOf, feedText, type FireIncident, type FireKind } from './fire-incidents.js';
import type { IdentifyRow } from './map-kml.js';

/** Decision 476: on (Thomson's switch decides who). False removes bushfire.io everywhere at the next release. */
export const BUSHFIRE_IO_ON: boolean = true;

/** The credits bushfire.io's tiles and site carry. */
export const BUSHFIRE_IO_CREDIT = 'Fires and fire aircraft: © Disaster Science (bushfire.io)';
export const FLIGHTRADAR24_CREDIT = 'Fire aircraft positions: © Flightradar24';

/** An aircraft quiet for longer is dropped (MB's app, unless "keep idle aircraft"). */
export const BUSHFIRE_IDLE_MINUTES = 10;
/** The tiles are regenerated about once a minute: the aircraft and incidents are asked as often. */
export const BUSHFIRE_POLL_MS = 60_000;

export interface BushfireTile {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

const MIN_ZOOM = 3;
const MAX_ZOOM = 12;
const MAX_TILES = 4;
const MAX_LAT = 85.0511;

const tileX = (lon: number, z: number): number =>
  Math.min(2 ** z - 1, Math.max(0, Math.floor(((lon + 180) / 360) * 2 ** z)));
const tileY = (lat: number, z: number): number => {
  const r = (Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
  return Math.min(2 ** z - 1, Math.max(0, y));
};

/**
 * The tiles for a view: the finest zoom (3 to 12) at which the view fits in at most four tiles; null when even zoom
 * 3 needs more (a view wider than about 90 degrees), or the view crosses the antimeridian.
 */
export function bushfireTilesFor(view: GeoBounds): BushfireTile[] | null {
  if (!(view.west < view.east && view.south < view.north)) return null;
  for (let z = MAX_ZOOM; z >= MIN_ZOOM; z--) {
    const x0 = tileX(view.west, z);
    const x1 = tileX(view.east, z);
    const y0 = tileY(view.north, z);
    const y1 = tileY(view.south, z);
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_TILES) continue;
    const out: BushfireTile[] = [];
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push({ z, x, y });
    return out;
  }
  return null;
}

// ------------------------------------------------------------------------------------------- the decoded tiles

/** A feature as Teslyn Field's Rust core decodes it (`teslyn_core::map::mvt::Feature`), positions `[lon, lat]`. */
export interface BushfireFeature {
  readonly id?: number;
  readonly geometry:
    | { readonly type: 'Point'; readonly coordinates: readonly (readonly [number, number])[] }
    | {
        readonly type: 'LineString';
        readonly coordinates: readonly (readonly (readonly [number, number])[])[];
      }
    | { readonly type: 'Polygon'; readonly coordinates: readonly (readonly (readonly [number, number])[])[] };
  readonly properties: Readonly<Record<string, unknown>>;
}

export interface BushfireTileAnswer {
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly fetched_at: string;
  readonly layers: Readonly<Record<string, readonly BushfireFeature[]>>;
}

/** The command's body: `{ "tiles": [...] }`, or null when it is not that. */
export function readBushfireTiles(body: string): BushfireTileAnswer[] | null {
  try {
    const v = JSON.parse(body) as { tiles?: unknown };
    return Array.isArray(v.tiles) ? (v.tiles as BushfireTileAnswer[]) : null;
  } catch {
    return null;
  }
}

const str = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** The `body` lines ("<strong>Nafc Callsign:</strong> BOMBER 598<br>") as label and value, text only, in order. */
export function bushfireBody(html: string): [string, string][] {
  return feedText(html)
    .split('\n')
    .map((l) => /^([^:]{1,40}):\s*(.*)$/.exec(l))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => [m[1]!.trim(), m[2]!.trim()] as [string, string])
    .filter(([, v]) => v !== '');
}

/** "BOMBER 598" as "Bomber 598" (MB's app shows the tactical call-sign title-cased). */
export function titleCaseCallsign(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** bushfire.io's lower-case registration ("vhodw", "n214lj") as Teslyn writes it ("VH-ODW", "N214LJ"). */
export function bushfireRegistration(reg: string): string | null {
  return normaliseRegistration(reg.toUpperCase());
}

const FT_PER_M = 3.280839895;
const KT_PER_KMH = 1 / 1.852;

/**
 * bushfire.io's aircraft (the `adsb` layer of every tile, an aircraft in two tiles kept once by `eKey`): registration,
 * transponder callsign, NAFC call-sign, category and role, make, model and operator, altitude in feet, speed in knots,
 * track; a helicopter by its Type Code or an RW category. Aircraft quiet for more than 10 minutes are left out.
 */
export function parseBushfireAircraft(body: string, nowMs: number): Parsed<TrackedPosition[]> {
  const tiles = readBushfireTiles(body);
  if (!tiles) return { value: [], problem: 'The bushfire.io answer could not be read.' };
  const seen = new Set<string>();
  const out: TrackedPosition[] = [];
  for (const t of tiles)
    for (const f of t.layers.adsb ?? []) {
      const p = f.properties;
      const key = str(p.eKey) || str(p.reg);
      if (!key || seen.has(key) || f.geometry.type !== 'Point') continue;
      seen.add(key);
      const at = f.geometry.coordinates[0];
      if (!at) continue;
      const idle = num(p.minsSinceActive);
      if (idle !== null && idle > BUSHFIRE_IDLE_MINUTES) continue;
      const lines = new Map(bushfireBody(str(p.body)));
      const reg = bushfireRegistration(str(p.reg) || (lines.get('Registration') ?? ''));
      const nafcCallsign = lines.get('Nafc Callsign') ?? '';
      const category = lines.get('Nafc Category') ?? '';
      const typeCode = lines.get('Type Code') ?? '';
      const callsign = (lines.get('Callsign') ?? '').toUpperCase().replace(/\s+/g, '');
      const timeMs = Date.parse(str(p.timestampPosition) || str(p.timestamp));
      const altM = num(p.altM);
      const spdKm = num(p.spdKm);
      const kind =
        /^helicopter/i.test(typeCode) || /^RW\b/i.test(category)
          ? 'helicopter'
          : /^landplane|^amphibian|^seaplane/i.test(typeCode) || /^FW\b/i.test(category)
            ? 'fixed_wing'
            : null;
      const pos: TrackedPosition = {
        source: 'bushfireio',
        account: '',
        deviceId: key,
        // the transponder callsign, as the other feeds label the aircraft; else the registration
        label: callsign || reg || titleCaseCallsign(nafcCallsign) || key,
        registration: reg,
        icaoHex: normaliseHex(str(p.hex)),
        kind,
        fleetGroup: '',
        sos: false,
        satellite: null,
        providerFlying: null,
        tripId: null,
        lat: at[1],
        lon: at[0],
        timeMs,
        altFt: altM !== null ? Math.round(altM * FT_PER_M) : null,
        speedKt: spdKm !== null ? Math.round(spdKm * KT_PER_KMH * 10) / 10 : null,
        headingDeg: num(p.trak),
        ...(nafcCallsign
          ? {
              fire: {
                callsign: nafcCallsign,
                registration: reg ?? '',
                category,
                make: lines.get('Manufacturer') ?? '',
                model: lines.get('Model') ?? '',
                ...(lines.get('Nafc Role') ? { role: lines.get('Nafc Role')! } : {}),
                ...(lines.get('Operator') ? { operator: lines.get('Operator')! } : {}),
              },
            }
          : {}),
      };
      if (validFix(pos) && pos.timeMs <= nowMs + 60_000) out.push(pos);
    }
  return { value: out, problem: null };
}

// ------------------------------------------------------------------------------------------------- incidents

/** bushfire.io's `alertLevel` names. */
function bushfireLevelWords(level: string): string {
  switch (level) {
    case 'emergencyWarning':
      return 'Emergency Warning';
    case 'watchAndAct':
      return 'Watch and Act';
    case 'advice':
      return 'Advice';
    default:
      return 'No warning';
  }
}

const KEEP_CLASSIFICATIONS = new Set(['fire', 'smoke', 'burn']);

/**
 * bushfire.io's fire incidents and warnings (the `incidents` layer): the point and polygons sharing an `eKey` joined,
 * across tiles; fire and smoke events, and planned burns and burnt areas as planned burns. Marine, medical and other
 * events are left out.
 */
export function parseBushfireIncidents(body: string): { incidents: FireIncident[]; problem: string | null } {
  const tiles = readBushfireTiles(body);
  if (!tiles) return { incidents: [], problem: 'The bushfire.io answer could not be read.' };
  const byKey = new Map<
    string,
    { props: Readonly<Record<string, unknown>>; at: LatLng | null; areas: LatLng[][]; ringKeys: Set<string> }
  >();
  for (const t of tiles)
    for (const f of t.layers.incidents ?? []) {
      const p = f.properties;
      if (!KEEP_CLASSIFICATIONS.has(str(p.eventClassification))) continue;
      const key = str(p.eKey);
      if (!key) continue;
      const e = byKey.get(key) ?? { props: p, at: null, areas: [], ringKeys: new Set<string>() };
      if (f.geometry.type === 'Point' && f.geometry.coordinates[0] && !e.at) {
        const [lon, lat] = f.geometry.coordinates[0];
        e.at = { lat, lng: lon };
      } else if (f.geometry.type === 'Polygon') {
        for (const ring of f.geometry.coordinates) {
          const pts = ring.map(([lon, lat]) => ({ lat, lng: lon }));
          // the same ring clipped into two tiles is two pieces; an identical piece is kept once
          const rk = pts.map((q) => `${q.lat.toFixed(5)},${q.lng.toFixed(5)}`).join(' ');
          if (pts.length >= 4 && !e.ringKeys.has(rk)) {
            e.ringKeys.add(rk);
            e.areas.push(pts);
          }
        }
      }
      byKey.set(key, e);
    }
  const incidents: FireIncident[] = [];
  for (const [key, e] of byKey) {
    const p = e.props;
    const lines = bushfireBody(str(p.body));
    const type = lines.find(([l]) => l === 'Type')?.[1] ?? '';
    const cls = str(p.eventClassification);
    const eventClass = str(p.eventClass);
    const kind: FireKind =
      cls === 'burn' || eventClass === 'burnarea' || /planned burn|burnt area/i.test(type)
        ? 'planned_burn'
        : 'bushfire';
    let at = e.at;
    if (!at) {
      const box = (() => {
        try {
          const b = JSON.parse(str(p.bbox)) as number[];
          return Array.isArray(b) && b.length === 4 && b.every(Number.isFinite) ? b : null;
        } catch {
          return null;
        }
      })();
      if (box) at = { lat: (box[1]! + box[3]!) / 2, lng: (box[0]! + box[2]!) / 2 };
    }
    const levelWords = bushfireLevelWords(str(p.alertLevel));
    const details: IdentifyRow[] = [
      ...lines
        // the warning's whole message is long HTML; its first lines are kept
        .filter(([l]) => l.length <= 30)
        .slice(0, 12)
        .map(([label, value]) => ({ label, value })),
      ...(str(p.agency) ? [{ label: 'Agency', value: str(p.agency) }] : []),
    ];
    incidents.push({
      id: `bushfireio:${key}`,
      feed: 'bushfireio',
      title: str(p.title),
      kind,
      level: fireLevelOf(levelWords),
      levelText: levelWords === 'No warning' ? '' : levelWords,
      at,
      areas: e.areas,
      details: details.filter((d, i) => details.findIndex((x) => x.label === d.label) === i),
      updated: str(p.timestampUpdatedByAgency) || str(p.timestampReported) || null,
      url: str(p.attributionUrl) || null,
      agencyId: lines.find(([l]) => l === 'Agency ID')?.[1] ?? '',
    });
  }
  return { incidents, problem: null };
}

const EARTH_KM = 6371.0088;
function km(a: LatLng, b: LatLng): number {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLng = (b.lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

const sameTitle = (a: string, b: string): boolean =>
  a.trim().toLowerCase().replace(/\s+/g, ' ') === b.trim().toLowerCase().replace(/\s+/g, ' ');

/** The agency's own id of an official incident (Emergency WA's CAD id, the RFS's incident number). */
function officialIds(i: FireIncident): string[] {
  const out: string[] = [];
  if (i.cad) out.push(i.cad);
  const tail = i.id.split(':').pop() ?? '';
  if (/^\d+$/.test(tail)) out.push(tail);
  return out;
}

/**
 * bushfire.io's incidents that no state feed Teslyn read lists (the state feed's is shown where both have it): the
 * same agency id (Emergency WA's CAD id, the RFS's number), the same title within 25 km, or any official incident
 * within 1 km.
 */
export function dedupeBushfireIncidents(
  bushfire: readonly FireIncident[],
  official: readonly FireIncident[],
): FireIncident[] {
  const ids = new Set(official.flatMap(officialIds));
  return bushfire.filter((b) => {
    if (b.agencyId && ids.has(b.agencyId)) return false;
    // Emergency WA's incident titles carry the CAD id ("…, CAD-ID: 825512)")
    const cad = /CAD-ID:\s*(\d+)/.exec(b.title)?.[1];
    if (cad && ids.has(cad)) return false;
    if (!b.at) return !official.some((o) => sameTitle(o.title, b.title));
    return !official.some((o) => {
      const at = o.at;
      if (!at) return sameTitle(o.title, b.title);
      const d = km(at, b.at!);
      return d <= 1 || (d <= 25 && sameTitle(o.title, b.title));
    });
  });
}

/** The fire layer's incidents: the state feeds', then bushfire.io's that no state feed lists. */
export function withBushfireIncidents(
  official: readonly FireIncident[],
  bushfire: readonly FireIncident[],
): FireIncident[] {
  return [...official, ...dedupeBushfireIncidents(bushfire, official)];
}
